import { EntityApprovalStatus } from '../entity/types';
import { FundApprovalStatus, InvestmentAvailabilityStatusType } from '../fund/types';

export const KycStatus = {
  Pending: 'PENDING',
  InProgress: 'IN_PROGRESS',
  Approved: 'APPROVED',
  Rejected: 'REJECTED',
} as const;

export type KycStatus = (typeof KycStatus)[keyof typeof KycStatus];

export const AdminDashboardUserStatus = {
  Pending: 'Pending',
  SignedUp: 'SignedUp',
  WaitingForApproval: 'WaitingForApproval',
  VerifiedBasic: 'VerifiedBasic',
  VerifiedEntity: 'VerifiedEntity',
  Blocked: 'Blocked',
} as const;

export type AdminDashboardUserStatus = keyof typeof AdminDashboardUserStatus;

export const AdminDashboardInvitationType = {
  GammaTeam: 'GammaTeam',
  Entity: 'Entity',
  EntityShareholder: 'EntityShareholder',
  FundShareholder: 'FundShareholder',
} as const;

export type AdminDashboardInvitationType =
  (typeof AdminDashboardInvitationType)[keyof typeof AdminDashboardInvitationType];

export type AdminUserTag = {
  id: string;
  name: string;
  createdAt: string;
};

////////////////////////// USERS //////////////////////////

export type AttributionSource = 'automated' | 'manual';

export type CurrentIntroducer = {
  introducerUserId: string;
  introducerName: string | null;
  attributedAt: string;
  introductionType: 'New' | 'Existing';
  attributionSource: AttributionSource;
};

export type AdminUsersSortField = 'name' | 'email' | 'status' | 'verifiedAt' | 'createdAt';

export type AdminViewsUsersParams = {
  page?: number;
  limit?: number;
  sortBy?: AdminUsersSortField;
  sortDirection?: 'asc' | 'desc';
  search?: string;
  status?: AdminDashboardUserStatus[];
  entityId?: string;
  gammaTeam?: boolean[];
  hasEntityAffiliation?: boolean[];
  blocked?: boolean[];
  tags?: string[];
};

export type AdminViewsUser = {
  id: string;
  name: string | null;
  email: string;
  avatarUrl: string | null;
  statuses: AdminDashboardUserStatus[];
  kycStatus: KycStatus | null;
  entityAffiliation: string | null;
  isGammaTeam: boolean;
  invitationType: AdminDashboardInvitationType | null;
  invitationTypes: AdminDashboardInvitationType[];
  verifiedSince: string | null;
  createdAt: string;
  alias: string | null;
  isBlocked: boolean;
  blockedAt: string | null;
  blockReason: string | null;
  blockInternalReference: string | null;
  tags: string[];
  countryOfResidence: string | null;
  phoneNumber: string | null;
  nationalities: string[];
  city: string | null;
  isUsPerson: boolean | null;
  isPoliticallyExposedPerson: boolean | null;
  businessOrOccupation: string | null;
  netWorth: string | null;
  householdGrossIncome: string | null;
  fundsInvestedCount: number;
  totalAmountInvestedUsd: number;
  isIntroducer: boolean;
  questionnaireSubmitted: boolean;
  currentIntroducer: CurrentIntroducer | null;
};

export type AdminViewsUsersRes = {
  items: AdminViewsUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  appliedFilters?: Record<string, unknown>;
};

////////////////////////// ENTITIES //////////////////////////

export type AdminEntitiesSortField =
  | 'legalName'
  | 'shortName'
  | 'country'
  | 'type'
  | 'fundsCount'
  | 'status'
  | 'createdAt';

export type AdminEntityType = 'Trust' | 'Partnership' | 'Foundation' | 'Limited company';

export type AdminViewsEntitiesParams = {
  page?: number;
  limit?: number;
  sortBy?: AdminEntitiesSortField;
  sortDirection?: 'asc' | 'desc';
  search?: string;
  status?: EntityApprovalStatus[];
  type?: AdminEntityType[];
};

export type AdminViewsEntity = {
  id: string;
  fullName: string;
  shortName: string | null;
  logoUrl: string | null;
  country: string | null;
  stateRegion: string | null;
  type: AdminEntityType | null;
  city: string | null;
  zipCode: string | null;
  entityEmail: string | null;
  identificationNumber: string | null;
  fundsCount: number;
  teamMembersCount: number;
  status: EntityApprovalStatus;
  registrationDate: string | null;
  createdAt: string;
  isBlocked?: boolean;
  blockReason?: string | null;
  blockInternalReference?: string | null;
  blockedAt?: string | null;
};

export type AdminViewsEntitiesRes = {
  items: AdminViewsEntity[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  appliedFilters?: Record<string, unknown>;
};

////////////////////////// FUNDS //////////////////////////

export const ADMIN_FUNDS_SORTABLE_FIELDS = [
  'name',
  'status',
  'investmentAvailabilityStatus',
  'currency',
  'minInvestment',
  'legalStructure',
  'category',
  'maxFundCapacity',
  'projectedYield',
  'navStrikeFrequency',
  'fundInceptionDate',
  'lockupPeriod',
  'redemptionFrequency',
  'reportingFrequency',
  'investorsCount',
  'transactionsCount',
  'unitsIssued',
  'tvl',
  'updatedAt',
  'createdAt',
] as const;

export type AdminFundsSortField = (typeof ADMIN_FUNDS_SORTABLE_FIELDS)[number];

export type AdminViewsFundsParams = {
  page?: number;
  limit?: number;
  sortBy?: AdminFundsSortField;
  sortDirection?: 'asc' | 'desc';
  search?: string;
  status?: FundApprovalStatus[];
  entityId?: string;
  legalStructure?: string[];
  investmentAvailabilityStatus?: InvestmentAvailabilityStatusType[];
};

export type AdminViewsFund = {
  id: string;
  name: string;
  entityId: string;
  entityName: string;
  isEntityApproved: boolean;
  entityApprovalStatus: EntityApprovalStatus | null;
  thumbnailUrl: string | null;
  cardImageUrl: string | null;
  investorsCount: number;
  unitsIssued: number;
  tvl: number;
  currency: string | null;
  minInvestment: number | null;
  legalStructure: string | null;
  projectedYield: number | null;
  category: string | null;
  assets: string[] | null;
  strategies: string[] | null;
  maxFundCapacity: number | null;
  fundInceptionDate: string | null;
  navStrikeFrequency: string | null;
  lockupPeriod: string | null;
  redemptionFrequency: string | null;
  reportingFrequency: string | null;
  transactionsCount: number;
  status: FundApprovalStatus;
  investmentAvailabilityStatus: InvestmentAvailabilityStatusType | null;
  lastUpdated: string;
  createdAt: string;
  blockReason?: string | null;
  blockInternalReference?: string | null;
  blockedAt?: string | null;
};

export type AdminViewsFundsRes = {
  items: AdminViewsFund[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  appliedFilters?: Record<string, unknown>;
};

////////////////////////// KYC //////////////////////////

export type UserKycAddress = {
  subStreet: string | null;
  subStreetEn: string | null;
  street: string | null;
  streetEn: string | null;
  stateCode: string | null;
  town: string | null;
  townEn: string | null;
  postCode: string | null;
  country: string | null;
  formattedAddress: string | null;
  source: string | null;
};

export type AdminUserKycRes = {
  fullName: string | null;
  dateOfBirth: string | null;
  nationality: string | null;
  documentType: string | null;
  documentNumber: string | null;
  addresses: UserKycAddress[];
  proofOfAddressType: string | null;
  proofOfAddressDate: string | null;
  status: string | null;
  verifiedOn: string | null;
  provider: string | null;
  updatedAt: string;
};

////////////////////////// STATS //////////////////////////

export type ChartDataPoint = {
  date: string;
  count: number;
};

export type UserStatsRes = {
  dailyKycVerificationsLast7Days: ChartDataPoint[];
  dailyKycVerificationsLast7DaysCumulative: ChartDataPoint[];
  dailyKycVerificationsLast30Days: ChartDataPoint[];
  dailyKycVerificationsLast30DaysCumulative: ChartDataPoint[];
  dailyRegistrationsLast7Days: ChartDataPoint[];
  dailyRegistrationsLast7DaysCumulative: ChartDataPoint[];
  dailyRegistrationsLast30Days: ChartDataPoint[];
  dailyRegistrationsLast30DaysCumulative: ChartDataPoint[];
  dailyVerificationsLast7Days: ChartDataPoint[];
  dailyVerificationsLast7DaysCumulative: ChartDataPoint[];
  dailyVerificationsLast30Days: ChartDataPoint[];
  dailyVerificationsLast30DaysCumulative: ChartDataPoint[];
  kycVerifiedUsersLast7Days: number;
  kycVerifiedUsersLast30Days: number;
  newUsersLast7Days: number;
  newUsersLast30Days: number;
  totalKycVerifiedUsers: number;
  totalUsers: number;
  totalVerifiedUsers: number;
  verifiedUsersLast7Days: number;
  verifiedUsersLast30Days: number;
};
